import React, { useState } from "react";
import Seo from "../components/Seo";

// -----------------------
// Modal Component
// -----------------------
function FaceModal({ open, faceIndex, faces, onClose, onPrev, onNext }) {
  if (!open) return null;

  const face = faces[faceIndex];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          background: "#fff",
          padding: "1.5em",
          borderRadius: 12,
          boxShadow: "0 4px 18px rgba(0,0,0,0.25)",
          textAlign: "center",
          maxWidth: "90vw",
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            background: "rgba(123,30,58,0.85)",
            color: "#fff",
            border: "none",
            borderRadius: "50%",
            width: 34,
            height: 34,
            fontSize: 20,
            fontWeight: 900,
            cursor: "pointer",
            zIndex: 3,
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          &#10005;
        </button>

        {/* Left Arrow */}
        {faceIndex > 0 && (
          <button
            onClick={onPrev}
            style={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(123,30,58,0.85)",
              color: "#fff",
              border: "none",
              borderRadius: "50%",
              width: 38,
              height: 38,
              fontSize: 22,
              cursor: "pointer",
              zIndex: 2,
            }}
            aria-label="Previous face"
          >
            &#8592;
          </button>
        )}

        {/* Right Arrow */}
        {faceIndex < faces.length - 1 && (
          <button
            onClick={onNext}
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(123,30,58,0.85)",
              color: "#fff",
              border: "none",
              borderRadius: "50%",
              width: 38,
              height: 38,
              fontSize: 22,
              cursor: "pointer",
              zIndex: 2,
            }}
            aria-label="Next face"
          >
            &#8594;
          </button>
        )}

        {/* Image */}
        <img
          src={face.img}
          alt={face.name}
          style={{
            maxWidth: "85vw",
            maxHeight: "70vh",
            borderRadius: 10,
            marginBottom: 18,
            boxShadow: "0 2px 8px #0002",
            objectFit: "contain",
            background: "#f7f7f7",
          }}
        />

        {/* Caption */}
        <div>
          <div
            style={{
              fontWeight: 900,
              fontSize: "1.25rem",
              color: "#7b1e3a",
              marginBottom: 4,
            }}
          >
            {face.name}
          </div>
          <div
            style={{
              fontWeight: 700,
              color: "#e67c30",
              marginBottom: 8,
            }}
          >
            {face.role}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------
// Data
// -----------------------
const famousFaces = [
  {
    name: "Chaim Herzog (1918-1997)",
    role: "Israel's Longest Serving President",
    img: require("../assets/images/Chaim_Herzog.jpg"),
    desc: `Born in Belfast, Herzog was the son of Dr Isaac Herzog, rabbi on Somerton Road. He became Israel's President (1983-1993), a general, and a key figure in Israel's Six Day War victory.`,
  },
  {
    name: "Prof. Norman Nevin",
    role: "Professor Emeritus in Medical Genetics, Queen's University Belfast",
    img: require("../assets/images/Prof._Norman_Nevin.jpg"),
    desc: `A leading clinical geneticist with over 300 publications, known for his work on congenital abnormalities and genetic disorders.`,
  },
  {
    name: "Amy Carmichael (1867-1951)",
    role: "Missionary & Social Reformer",
    img: require("../assets/images/Amy_Carmichael.jpg"),
    desc: `Born in Millisle, Amy Carmichael was a missionary who worked with the poor and later protected vulnerable women and children in India.`,
  },
  {
    name: "Markey Robinson (1918-1999)",
    role: "Artist",
    img: require("../assets/images/Markey_Robinson.jpg"),
    desc: `A Belfast-born painter celebrated for his primitive style and colourful compositions shaped by his life in Belfast and Paris.`,
  },
  {
    name: "John Hewitt (1907-1987)",
    role: "Poet",
    img: require("../assets/images/John_Hewitt.jpg"),
    desc: `One of Belfast's most significant poets and the first writer-in-residence at Queen's University Belfast.`,
  },
];

// -----------------------
// Main Component
// -----------------------
export default function Famous() {
  const [modal, setModal] = useState({ open: false, faceIndex: 0 });

  return (
    <>
      <Seo
        title="Famous Faces of the Vine Centre | Community Impact"
        description="Discover notable people who have been part of the Vine Centre's story."
      />

      <section
        id="famous"
        style={{ maxWidth: 1100, margin: "0 auto", padding: "2.5em 1em" }}
      >
        <FaceModal
          open={modal.open}
          faceIndex={modal.faceIndex}
          faces={famousFaces}
          onClose={() => setModal({ open: false, faceIndex: 0 })}
          onPrev={(e) => {
            e.stopPropagation();
            setModal((m) => ({
              ...m,
              faceIndex: Math.max(0, m.faceIndex - 1),
            }));
          }}
          onNext={(e) => {
            e.stopPropagation();
            setModal((m) => ({
              ...m,
              faceIndex: Math.min(famousFaces.length - 1, m.faceIndex + 1),
            }));
          }}
        />

        <h2
          style={{
            color: "#7b1e3a",
            fontWeight: 900,
            fontSize: "2.2rem",
            marginBottom: 10,
          }}
        >
          Famous Faces at the Vine Centre
        </h2>

        <p style={{ color: "#3a6c5a", fontSize: "1.15rem", marginBottom: 32 }}>
          Notable people associated with the Vine Centre, featured in our
          stained glass window and as namesakes for our rooms.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "2.2em",
          }}
        >
          {famousFaces.map((face, idx) => (
            <div
              key={face.name}
              style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 4px 18px rgba(123,30,58,0.10)",
                padding: "1.5em 1em",
                textAlign: "center",
              }}
            >
              <img
                src={face.img}
                alt={face.name}
                style={{
                  height: 140,
                  width: 140,
                  objectFit: "cover",
                  borderRadius: "50%",
                  marginBottom: 18,
                  boxShadow: "0 2px 8px #7b1e3a22",
                  cursor: "pointer",
                }}
                onClick={() => setModal({ open: true, faceIndex: idx })}
                title="Click to enlarge"
              />

              <div
                style={{
                  fontWeight: 900,
                  fontSize: "1.13rem",
                  color: "#7b1e3a",
                  marginBottom: 4,
                }}
              >
                {face.name}
              </div>

              <div
                style={{
                  fontWeight: 700,
                  color: "#e67c30",
                  marginBottom: 8,
                }}
              >
                {face.role}
              </div>

              <div style={{ color: "#3a6c5a", fontSize: "1.01rem" }}>
                {face.desc}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
